/**
 * Gift-with-purchase: auto add/remove/sync free gifts for battery purchases.
 *
 * Rules live in snippets/sb-gift-rules.liquid (rendered into a <script id="sb-gift-rules">
 * JSON tag by layout/theme.liquid) — that file is the single source of truth, also consumed
 * by sections/main-product.liquid to display the matching gift(s) on the product page. Edit
 * rules there, not here, so the cart behavior and the product-page display never drift apart.
 *
 * The gift uses the SAME variant customers can buy normally. Auto-added gift lines are
 * tagged with hidden line item properties (_free_gift, _gift_source) so they are a distinct
 * cart line from any manually-added, full-price P1 the customer adds themselves. Dawn's cart
 * templates already hide properties whose key starts with "_" from the customer-facing UI.
 *
 * State is never cached locally — every sync re-reads /cart.js and reconciles from that, so a
 * failed request can't leave a duplicate gift or a stuck cart; the next cart-update event just
 * re-evaluates from the server's actual state.
 */
(function () {
  'use strict';

  function loadGiftRules() {
    var tag = document.getElementById('sb-gift-rules');
    if (!tag) return [];
    try {
      var rules = JSON.parse(tag.textContent);
      return rules.map(function (rule) {
        return {
          triggerProductId: rule.trigger_product_id,
          giftVariantId: rule.gift_variant_id,
          giftSource: rule.gift_source,
          choiceGroup: rule.choice_group || '',
          giftMultiplier: rule.gift_multiplier || 1,
          minQty: rule.min_qty || 1,
          maxQty: rule.max_qty || 0,
        };
      });
    } catch (error) {
      console.error('gift-with-purchase: failed to parse #sb-gift-rules', error);
      return [];
    }
  }

  var GIFT_RULES = loadGiftRules();

  // First choice group per trigger product, in rule (block) order — the same option
  // sections/sb-gift-rules.liquid pre-checks on the product page. A trigger line added
  // without a _gift_choice property (collection-page / quick add, which never renders the
  // gift radios) counts toward this default, so it still gets the gift the product page
  // would have given by default.
  var DEFAULT_CHOICE_BY_TRIGGER = {};
  GIFT_RULES.forEach(function (rule) {
    if (rule.choiceGroup && !(rule.triggerProductId in DEFAULT_CHOICE_BY_TRIGGER)) {
      DEFAULT_CHOICE_BY_TRIGGER[rule.triggerProductId] = rule.choiceGroup;
    }
  });

  var isSyncing = false;
  var needsResync = false;

  function fetchCart() {
    return fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(function (res) {
      if (!res.ok) throw new Error('gift-with-purchase: failed to fetch cart');
      return res.json();
    });
  }

  function postJSON(url, payload) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (res) {
      if (!res.ok) throw new Error('gift-with-purchase: request to ' + url + ' failed');
      return res.json();
    });
  }

  // Counts trigger-product quantity toward a rule. A blank choiceGroup (today's only
  // shape, before choice groups existed) matches every cart line for the trigger product,
  // same as before. A non-blank choiceGroup only counts lines the customer tagged with
  // that choice via the _gift_choice property (set by assets/sb-gift-choice.js on the
  // product form; lines without it fall back to DEFAULT_CHOICE_BY_TRIGGER) — so switching options on the product page and re-adding to cart makes
  // the old option's needed quantity drop to 0 and the new option's rise, letting the
  // existing add/adjust/remove logic below handle the swap with no special-casing.
  function triggerQuantityForRule(cart, rule) {
    return cart.items
      .filter(function (item) {
        if (item.product_id !== rule.triggerProductId) return false;
        if (!rule.choiceGroup) return true;
        var choice = item.properties && item.properties._gift_choice;
        if (!choice) return rule.choiceGroup === DEFAULT_CHOICE_BY_TRIGGER[rule.triggerProductId];
        return choice === rule.choiceGroup;
      })
      .reduce(function (sum, item) {
        return sum + item.quantity;
      }, 0);
  }

  function giftLines(cart, giftVariantId, giftSource) {
    return cart.items.filter(function (item) {
      return (
        item.variant_id === giftVariantId &&
        item.properties &&
        item.properties._free_gift === 'true' &&
        item.properties._gift_source === giftSource
      );
    });
  }

  function addGifts(items) {
    return postJSON(window.routes.cart_add_url, { items: items });
  }

  function changeLineByKey(key, quantity) {
    return postJSON(window.routes.cart_change_url, { id: key, quantity: quantity });
  }

  function refreshCartIconBubble() {
    return fetch(window.routes.cart_url + '?section_id=cart-icon-bubble', { cache: 'no-store' })
      .then(function (res) {
        return res.ok ? res.text() : null;
      })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var source = doc.getElementById('cart-icon-bubble');
        var target = document.getElementById('cart-icon-bubble');
        if (source && target) target.innerHTML = source.innerHTML;
      })
      .catch(function () {
        /* non-critical, cart-items/cart-drawer-items still refresh via cartUpdate below */
      });
  }

  function refreshMainCartFooter() {
    var footer = document.getElementById('main-cart-footer');
    if (!footer) return Promise.resolve();

    return fetch(window.routes.cart_url + '?section_id=' + encodeURIComponent(footer.dataset.id), { cache: 'no-store' })
      .then(function (res) {
        return res.ok ? res.text() : null;
      })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var source = doc.querySelector('#main-cart-footer .js-contents');
        var target = footer.querySelector('.js-contents');
        if (source && target) target.innerHTML = source.innerHTML;
      })
      .catch(function (error) {
        console.error(error);
      });
  }

  // Reconciles a single rule against the already-fetched cart snapshot, without making any
  // network call itself. A missing gift line is returned as an { type: 'add' } plan instead
  // of being posted here — the caller batches every rule's missing gift into ONE /cart/add.js
  // call, so a trigger product with multiple gift rules (e.g. two blocks on the same product)
  // costs a single round trip instead of one sequential POST per rule. That matters because a
  // customer who navigates away mid-sync (e.g. clicking "view cart" from the add-to-cart
  // popup) can abort a still-in-flight request — with N sequential adds, only the earlier ones
  // are guaranteed to have been sent before that happens.
  function planRule(cart, rule) {
    var triggerQty = triggerQuantityForRule(cart, rule);
    var needed = Math.floor(triggerQty / rule.minQty) * rule.giftMultiplier;
    if (rule.maxQty > 0 && needed > rule.maxQty) needed = rule.maxQty;
    var lines = giftLines(cart, rule.giftVariantId, rule.giftSource);
    var current = lines.reduce(function (sum, line) {
      return sum + line.quantity;
    }, 0);

    if (needed === current) return null;

    if (lines.length === 0) {
      if (needed === 0) return null;
      return {
        type: 'add',
        item: {
          id: rule.giftVariantId,
          quantity: needed,
          properties: {
            _free_gift: 'true',
            _gift_source: rule.giftSource,
          },
        },
      };
    }

    // Adjust the first gift line to match; collapse any extra lines (defensive only —
    // Shopify merges identical variant+properties into one line under normal operation).
    var primary = lines[0];
    var extras = lines.slice(1);
    return {
      type: 'change',
      run: function () {
        var work = Promise.resolve();
        if (primary.quantity !== needed) {
          work = work.then(function () {
            return changeLineByKey(primary.key, needed);
          });
        }
        extras.forEach(function (extra) {
          work = work.then(function () {
            return changeLineByKey(extra.key, 0);
          });
        });
        return work;
      },
    };
  }

  // Cart update events do not share one response shape: product-form publishes the
  // /cart/add.js line-item response while cart-items publishes a complete cart. Always
  // re-read /cart.js so every gift rule reconciles against the full server-side cart.
  // Resolves with `true` if any gift line was added or adjusted.
  function performSync() {
    return fetchCart().then(function (cart) {
      var plans = GIFT_RULES.map(function (rule) {
        return planRule(cart, rule);
      }).filter(Boolean);

      var addItems = plans
        .filter(function (plan) {
          return plan.type === 'add';
        })
        .map(function (plan) {
          return plan.item;
        });

      var changeWork = plans
        .filter(function (plan) {
          return plan.type === 'change';
        })
        .reduce(function (chain, plan) {
          return chain.then(plan.run);
        }, Promise.resolve());

      var addWork = addItems.length ? addGifts(addItems) : Promise.resolve(null);

      return Promise.all([addWork, changeWork]).then(function () {
        return plans.length > 0;
      });
    });
  }

  function syncGifts() {
    if (isSyncing) {
      needsResync = true;
      return;
    }
    isSyncing = true;

    performSync()
      .then(function (changed) {
        if (!changed) return;
        // Icon bubble refresh and the re-render-triggering publish are independent of each
        // other — run them concurrently instead of serially to shave another round trip.
        var tasks = [refreshCartIconBubble(), refreshMainCartFooter()];
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          tasks.push(publish(PUB_SUB_EVENTS.cartUpdate, { source: 'gift-sync' }));
        }
        return Promise.all(tasks);
      })
      .catch(function (error) {
        console.error(error);
      })
      .finally(function () {
        isSyncing = false;
        if (needsResync) {
          needsResync = false;
          syncGifts();
        }
      });
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'gift-sync') return;
      syncGifts();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncGifts);
  } else {
    syncGifts();
  }

  // Exposed so assets/sb-product.js (the "Buy it now" flow) can add the item to cart via
  // its own fetch, then await this directly before redirecting to checkout — bypassing this
  // file's own pub/sub-triggered path entirely, so there's no race between the two.
  window.SBGiftSync = performSync;
})();
